import { PlusOutlined } from '@ant-design/icons';
import { Dropdown, Menu, MenuProps, Skeleton, Space, Typography, theme } from 'antd';
import dynamic from 'next/dynamic';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DEDUP_INTERVAL_DEFAULT } from '../../constants/swr-config';
import useRecentTickets, { RecentTicket } from '../../hooks/useRecentTickets';
import useToggle from '../../hooks/useToggle';
import { pushTo } from '../../utils/routeHelper';
import { getTabStore } from '../../utils/store/getStore';
import MiniTag from '../MiniTag';
import { OrganizationModalProps } from '../OrganizationDetailPage/OrganizationCreateModal';
import RefreshButton from '../RefreshButton';
import { UserModalProps } from '../UserModal';
const DynamicUserModal = dynamic(() => import('../UserModal')) as FC<UserModalProps>;
const DynamicOrganizationModal = dynamic(
    () => import('../OrganizationDetailPage/OrganizationCreateModal')
) as FC<OrganizationModalProps>;

const menuStyle = {
    boxShadow: 'none',
    width: 250
};

function MinimalTicketInfo({ ticket }: { ticket: RecentTicket }) {
    const {
        token: { fontSize }
    } = theme.useToken();

    const { status, subject, ticketKey, requester } = ticket;

    const secondaryInfo = `#${ticketKey.split('-').pop()} • ${requester}`;

    return (
        <Space align="start">
            <MiniTag mini status={status} />
            <Space direction="vertical" size={0} style={{ maxWidth: '20ch' }}>
                <Typography.Text ellipsis>{subject}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: fontSize - 2 }}>
                    {secondaryInfo}
                </Typography.Text>
            </Space>
        </Space>
    );
}

function RecentTicketsSection() {
    const { t } = useTranslation('common');
    const { data, isLoading, mutate } = useRecentTickets({
        revalidateOnMount: true,
        dedupingInterval: DEDUP_INTERVAL_DEFAULT
    });

    const items = [
        {
            key: 'recent-tickets',
            type: 'group',
            label: (
                <Space>
                    {t('recentTickets')}
                    <RefreshButton onRefreshButtonClick={() => mutate()} />
                </Space>
            ),
            children: [
                { key: 'divider-2', type: 'divider' },
                ...(isLoading
                    ? [
                          {
                              key: 'loading-menu-item',
                              label: <Skeleton active paragraph={{ rows: 3 }} title={false} />
                          }
                      ]
                    : (data ?? []).slice(0, 5).map((t) => ({
                          key: t.ticketKey,
                          label: <MinimalTicketInfo ticket={t} />,
                          onClick: () => pushTo(`/tickets/${t.ticketKey}`)
                      })))
            ]
        }
    ];
    return <Menu style={menuStyle} mode="vertical" items={items} />;
}

enum AddButtonAction {
    'add-ticket' = 'add-ticket',
    'add-user' = 'add-user',
    'add-organization' = 'add-organization'
}

async function goToNewTicketPage() {
    const url = getTabStore().addNewTicketTabAndGetTargetUrl();
    pushTo(url);
}
export default function AddButton() {
    const {
        token: { colorBgElevated, borderRadiusLG, boxShadowSecondary, padding }
    } = theme.useToken();
    const { t } = useTranslation('common');
    const [isUserModalOpen, , openUserModal, closeUserModal] = useToggle(false);
    const [isOrganizationModalOpen, , openOrganizationModal, closeOrganizationModal] = useToggle(false);

    const onClickHandler: MenuProps['onClick'] = ({ key }) => {
        switch (key) {
            case AddButtonAction['add-ticket']:
                goToNewTicketPage();
                break;
            case AddButtonAction['add-user']:
                openUserModal();
                break;
            case AddButtonAction['add-organization']:
                openOrganizationModal();
                break;
            default:
                break;
        }
        return;
    };

    const items: MenuProps['items'] = [
        {
            key: 'add',
            type: 'group',
            label: t('addMenu.new'),
            children: [
                { key: 'divider', type: 'divider' },
                {
                    key: AddButtonAction['add-ticket'],
                    label: t('addMenu.ticket')
                },
                {
                    key: AddButtonAction['add-user'],
                    label: t('addMenu.user')
                },
                {
                    key: AddButtonAction['add-organization'],
                    label: t('addMenu.organization')
                }
            ]
        }
    ];

    const contentStyle = {
        backgroundColor: colorBgElevated,
        borderRadius: borderRadiusLG,
        boxShadow: boxShadowSecondary
    };

    return (
        <>
            <Dropdown
                menu={{ items, onClick: onClickHandler }}
                trigger={['hover']}
                destroyPopupOnHide
                dropdownRender={(menu) => (
                    <div style={contentStyle}>
                        {React.cloneElement(menu as React.ReactElement, { style: menuStyle })}
                        <RecentTicketsSection />
                    </div>
                )}
                // onOpenChange={onOpenChange}
            >
                <Space
                    className="full-width full-height"
                    style={{ padding: padding / 2 }}
                    onClick={() => goToNewTicketPage()}
                >
                    <PlusOutlined />
                    {t('add')}
                </Space>
            </Dropdown>
            {isUserModalOpen && <DynamicUserModal isOpen={isUserModalOpen} closeUserModal={closeUserModal} />}
            {isOrganizationModalOpen && (
                <DynamicOrganizationModal
                    isOpen={isOrganizationModalOpen}
                    closeOrganizationModal={closeOrganizationModal}
                />
            )}
        </>
    );
}
